// Derivaciones de Originación — lógica PURA, sin React.
//
// Todo lo de aquí existe porque el silo manda lo mínimo y el mock pide más:
//   · el KPI de captura necesita el desglose otras/contado, y el silo solo manda el total → se suma
//     la serie diaria, que sí lo trae partido
//   · la mezcla llega como celdas sueltas por financiera → se vuelve segmentos con su participación
//   · el ranking llega ordenado por volumen SK y la tabla se puede reordenar por cualquier columna
import type {
    OriginationDailyPoint,
    OriginationFinancierCell,
    OriginationRiskMix,
    RiskLevelCell,
    StoreOriginationRow,
} from '@/api/services/retail-wizard/backoffice';

/** Los tres canales del cuadro de tendencia, en el orden en que se apilan (SK abajo). */
export type TrendChannel = 'sureKeep' | 'otherFinanciers' | 'cash';

/** El toggle del cuadro de tendencia: piezas o dinero. */
export type TrendMode = 'units' | 'cents';

/** Piezas y monto sumados de una serie diaria, partidos por canal. */
export interface ChannelSummary {
    sureKeepUnits: number;
    otherFinanciersUnits: number;
    cashUnits: number;
    totalUnits: number;
    sureKeepCents: number;
}

/**
 * Suma la serie diaria por canal.
 *
 * El KPI «Captura del mercado» solo trae `sureKeepUnits` y `totalUnits`, y el mock pide decir
 * cuántas fueron con OTRAS financieras y cuántas de contado. La resta `total − SK` mezclaría las
 * dos en un número que no significa nada; la serie diaria sí las trae separadas.
 */
export function summarizeChannels(daily: OriginationDailyPoint[]): ChannelSummary {
    return daily.reduce<ChannelSummary>(
        (acc, point) => ({
            sureKeepUnits: acc.sureKeepUnits + point.sureKeep.units,
            otherFinanciersUnits: acc.otherFinanciersUnits + point.otherFinanciers.units,
            cashUnits: acc.cashUnits + point.cash.units,
            totalUnits: acc.totalUnits + point.total.units,
            sureKeepCents: acc.sureKeepCents + point.sureKeep.cents,
        }),
        {
            sureKeepUnits: 0,
            otherFinanciersUnits: 0,
            cashUnits: 0,
            totalUnits: 0,
            sureKeepCents: 0,
        },
    );
}

/** El valor de un canal en un día, según el toggle de piezas/pesos. */
export function trendValue(
    point: OriginationDailyPoint,
    channel: TrendChannel,
    mode: TrendMode,
): number {
    return mode === 'units' ? point[channel].units : point[channel].cents;
}

/** El techo del eje: el día más alto de la serie. Nunca cero, o la escala se indefine. */
export function trendMax(daily: OriginationDailyPoint[], mode: TrendMode): number {
    const max = Math.max(
        0,
        ...daily.map((point) => (mode === 'units' ? point.total.units : point.total.cents)),
    );
    return max === 0 ? 1 : max;
}

/** Etiquetas cortas de las financieras, como el resto del portal: SK · PJ · CY · LP · IV. */
const FINANCIER_SHORT: Record<string, string> = {
    SUREKEEP: 'SK',
    PAYJOY: 'PJ',
    CREDIYA: 'CY',
    LESPAGO: 'LP',
    INNOVA: 'IV',
};

/** Nombres largos para el `title` de la barra: la sigla sola no informa a quien llega nuevo. */
const FINANCIER_NAME: Record<string, string> = {
    SUREKEEP: 'SureKeep',
    PAYJOY: 'PayJoy',
    CREDIYA: 'Crediya',
    LESPAGO: 'Lespago',
    INNOVA: 'Innova',
};

/** La sigla de una financiera. La que el portal no conoce se pinta con su llave, no con un hueco. */
export function financierShort(financier: string): string {
    return FINANCIER_SHORT[financier] ?? financier;
}

export function financierName(financier: string): string {
    return FINANCIER_NAME[financier] ?? financier;
}

/** Un tramo de la barra de mezcla: quién, cuántas piezas y qué parte del total de la tienda. */
export interface MixSegment {
    key: string;
    label: string;
    units: number;
    /** 0–100. Es participación DENTRO de la tienda, no del silo. */
    sharePct: number;
}

/** La llave del tramo de las derivadas sin proveedor registrado. */
export const UNKNOWN_FINANCIER_KEY = 'UNKNOWN';

/**
 * Arma los tramos de la barra de mezcla de una tienda.
 *
 * 🔴 Las derivadas SIN proveedor registrado entran como un tramo propio. Omitirlas repartiría su
 * peso entre las financieras conocidas y cada una se pintaría más grande de lo que es.
 *
 * SureKeep va primero —es el sujeto de la pantalla y el mismo orden que apila el cuadro de
 * tendencia—, el resto en orden alfabético, y las sin proveedor al final por ser el residuo.
 */
export function mixSegments(row: StoreOriginationRow): MixSegment[] {
    const cells: OriginationFinancierCell[] = row.financierMix.filter((cell) => cell.units > 0);
    const total = cells.reduce((sum, cell) => sum + cell.units, 0) + row.unknownFinancierUnits;
    if (total === 0) return [];

    const known = [...cells].sort((a, b) => {
        if (a.financier === 'SUREKEEP') return -1;
        if (b.financier === 'SUREKEEP') return 1;
        return financierName(a.financier).localeCompare(financierName(b.financier), 'es-MX');
    });

    const segments = known.map((cell) => ({
        key: cell.financier,
        label: financierName(cell.financier),
        units: cell.units,
        sharePct: (cell.units / total) * 100,
    }));

    if (row.unknownFinancierUnits > 0) {
        segments.push({
            key: UNKNOWN_FINANCIER_KEY,
            label: 'Financiera sin registrar',
            units: row.unknownFinancierUnits,
            sharePct: (row.unknownFinancierUnits / total) * 100,
        });
    }
    return segments;
}

// ── Cómo originamos (C5) ─────────────────────────────────────────────────────────────────────────

/**
 * Los tres niveles en el orden del mock: de más riesgo a menos.
 *
 * Es una ESCALA ordinal, no una lista: Bronce · Plata · Oro se lee como una progresión y ordenarla
 * alfabéticamente (Bronce · Oro · Plata) rompe lo que el cuadro quiere decir.
 */
export const RISK_LEVEL_ORDER = ['BRONZE', 'SILVER', 'GOLD'] as const;

const RISK_LEVEL_LABEL: Record<string, string> = {
    BRONZE: 'Bronce',
    SILVER: 'Plata',
    GOLD: 'Oro',
    NOT_ELIGIBLE: 'No elegible',
};

/** La paleta del mock, misma para la barra y la leyenda. */
const RISK_LEVEL_COLOR: Record<string, string> = {
    BRONZE: 'hsl(25 60% 72%)',
    SILVER: 'hsl(210 15% 78%)',
    GOLD: 'hsl(40 75% 68%)',
};

const RISK_LEVEL_TEXT: Record<string, string> = {
    BRONZE: 'hsl(25 55% 25%)',
    SILVER: 'hsl(210 15% 25%)',
    GOLD: 'hsl(40 75% 22%)',
};

const UNKNOWN_LEVEL_COLOR = 'hsl(215 16% 65%)';

/** El nombre de un nivel. Uno que el portal no conozca se pinta con su llave, no con un hueco. */
export function riskLevelLabel(level: string): string {
    return RISK_LEVEL_LABEL[level] ?? level;
}

export function riskLevelColor(level: string): string {
    return RISK_LEVEL_COLOR[level] ?? UNKNOWN_LEVEL_COLOR;
}

export function riskLevelTextColor(level: string): string {
    return RISK_LEVEL_TEXT[level] ?? 'hsl(215 16% 20%)';
}

/**
 * Ordena las barras por la escala de riesgo, con las llaves desconocidas al final.
 *
 * El silo puede mandar los niveles en cualquier orden; el cuadro los tiene que leer siempre igual o
 * la barra cambia de forma entre ventanas sin que los datos hayan cambiado.
 */
export function orderRiskLevels(levels: RiskLevelCell[]): RiskLevelCell[] {
    const rank = (level: string) => {
        const index = RISK_LEVEL_ORDER.indexOf(level as (typeof RISK_LEVEL_ORDER)[number]);
        return index === -1 ? RISK_LEVEL_ORDER.length : index;
    };
    return [...levels].sort((a, b) => {
        const delta = rank(a.level) - rank(b.level);
        return delta !== 0
            ? delta
            : riskLevelLabel(a.level).localeCompare(riskLevelLabel(b.level), 'es-MX');
    });
}

/** Un tramo de la barra de niveles: cuánto pesa y si su porcentaje cabe escrito adentro. */
export interface RiskBarSegment {
    level: string;
    label: string;
    credits: number;
    /** 0–100, calculado sobre los créditos CON nivel. Los empleados no entran en la base. */
    sharePct: number;
    /** El mock solo escribe el número dentro del tramo cuando pasa del 10 %: abajo no cabe. */
    showLabel: boolean;
}

/**
 * Arma los tramos de la barra por nivel.
 *
 * La participación se recalcula sobre los créditos que de verdad viajaron en `levels`, no sobre
 * `totalLeveled`: si el silo recorta una barra, las que quedan tienen que sumar 100 %.
 */
export function riskBarSegments(mix: OriginationRiskMix): RiskBarSegment[] {
    const ordered = orderRiskLevels(mix.levels).filter((cell) => cell.credits > 0);
    const total = ordered.reduce((sum, cell) => sum + cell.credits, 0);
    if (total === 0) return [];
    return ordered.map((cell) => {
        const sharePct = (cell.credits / total) * 100;
        return {
            level: cell.level,
            label: riskLevelLabel(cell.level),
            credits: cell.credits,
            sharePct,
            showLabel: sharePct > 10,
        };
    });
}

/**
 * `true` si la mitad izquierda de C5 tiene algo real que pintar.
 *
 * Ya no mira `mix.scatter`: esa nube era la de un crédito por punto y la reemplazó el scatter de
 * tiendas, que viaja fuera del `riskMix` y se pregunta aparte.
 */
export function hasRiskMix(mix: OriginationRiskMix | undefined): boolean {
    if (!mix) return false;
    return (
        mix.levels.some((cell) => cell.credits > 0) ||
        mix.employeeCredits > 0 ||
        mix.unknownLevelCredits > 0
    );
}

/** `48` — el SCI es un entero de 0 a 100; el decimal no aporta y desalinea la columna. */
export function formatSci(sci: number): string {
    return String(Math.round(sci));
}

/** Las columnas por las que se puede ordenar el ranking. */
export type RankingColumn =
    | 'storeName'
    | 'sureKeepUnits'
    | 'capturePct'
    | 'downPaymentAvgPct'
    | 'firstInstallmentCohortPct'
    | 'captureChangePp';

export type SortDirection = 'asc' | 'desc';

/** La etiqueta corta de una tienda cuando el silo no manda nombre: el ULID crudo no informa. */
export function storeLabel(row: StoreOriginationRow): string {
    return row.storeName ?? `Tienda ${row.storeId.slice(-6)}`;
}

/**
 * Ordena el ranking por la columna pedida.
 *
 * Los `null` van SIEMPRE al final, suba o baje el orden: «no hay base para calcular» no es un valor
 * chico, y dejarlo competir como `-Infinity` lo pintaba como la peor tienda de la red.
 */
export function sortRanking(
    rows: StoreOriginationRow[],
    column: RankingColumn,
    direction: SortDirection,
): StoreOriginationRow[] {
    const sign = direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        if (column === 'storeName') {
            return storeLabel(a).localeCompare(storeLabel(b), 'es-MX') * sign;
        }
        const valueA = numericValue(a, column);
        const valueB = numericValue(b, column);
        if (valueA === null && valueB === null) return 0;
        if (valueA === null) return 1;
        if (valueB === null) return -1;
        return (valueA - valueB) * sign;
    });
}

function numericValue(
    row: StoreOriginationRow,
    column: Exclude<RankingColumn, 'storeName'>,
): number | null {
    if (column === 'sureKeepUnits') return row.channels.sureKeep.units;
    return row[column];
}

/** Por qué el silo no mandó porcentaje. `null` tiene tres causas y no se pintan igual. */
export type ChangeState =
    | { kind: 'pct'; pct: number }
    | { kind: 'fromZero' }
    | { kind: 'noActivity' }
    | { kind: 'noBaseline' };

/**
 * Clasifica la variación con la base que el silo SÍ manda (`previousUnits`).
 * Con base en cero y ventas hoy hay crecimiento real, aunque no haya porcentaje que calcular.
 */
export function changeState(
    changePct: number | null,
    current: number,
    previous: number | null,
): ChangeState {
    if (changePct !== null) return { kind: 'pct', pct: changePct };
    if (previous !== 0) return { kind: 'noBaseline' };
    return current > 0 ? { kind: 'fromZero' } : { kind: 'noActivity' };
}

// ── Formato ──────────────────────────────────────────────────────────────────────────────────────

/** Piezas con separador de miles. `1234` pegado a otras columnas numéricas se lee mal. */
export function formatUnits(units: number): string {
    return units.toLocaleString('es-MX');
}

/** `$12.3K` · `$1.2M` · `$450`. Centavos entran, pesos salen. */
export function moneyShort(cents: number): string {
    const pesos = cents / 100;
    const abs = Math.abs(pesos);
    if (abs >= 1_000_000) return `$${(pesos / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(pesos / 1_000).toFixed(1)}K`;
    return `$${String(Math.round(pesos))}`;
}

/**
 * Un porcentaje del silo, o guion.
 *
 * 🔴 Ya viene en escala de porcentaje (`42.3` = 42.3 %): acá NO se re-escala. Y `null` es guion,
 * nunca un `0%` que en realidad es «no hay base que dividir».
 */
export function formatPct(pct: number | null): string {
    return pct === null ? '—' : `${String(pct)}%`;
}

/** `+12%` / `-8%` / `0%`. El `+` sale solo cuando de verdad subió: un cero no afirma dirección. */
export function formatChangePct(pct: number): string {
    return `${pct > 0 ? '+' : ''}${String(pct)}%`;
}

/** `+3.4 pp` — puntos porcentuales, que NO son un porcentaje relativo. */
export function formatPp(pp: number): string {
    return `${pp > 0 ? '+' : ''}${String(pp)} pp`;
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * `25/ago/2026` — el mes con letras, nunca con número.
 *
 * `day` ya viene como el día del NEGOCIO (`YYYY-MM-DD`, cortado en CDMX): se parte el string en vez
 * de construir un `Date`, porque `new Date('2026-08-25')` es medianoche UTC y en México cae el 24.
 */
export function formatBusinessDay(day: string): string {
    const [year, month, date] = day.split('-');
    const index = Number(month) - 1;
    if (!year || !date || index < 0 || index > 11) return day;
    return `${date}/${MONTHS[index]}/${year}`;
}

/** `25/ago` — la versión corta del eje del cuadro, donde el año no cabe y no aporta. */
export function formatAxisDay(day: string): string {
    const [, month, date] = day.split('-');
    const index = Number(month) - 1;
    if (!date || index < 0 || index > 11) return day;
    return `${date}/${MONTHS[index]}`;
}

/**
 * La ventana que el silo leyó: `28/may/2026 – 25/ago/2026`.
 *
 * 🔴 Se formatea en **CDMX**, no en UTC. El silo corta el día en medianoche de México, así que el
 * `to` de hoy es `T05:59:59.999Z` del día SIGUIENTE — leerlo en UTC corre la etiqueta un día.
 */
export function formatWindow(from: string, to: string): string {
    const start = formatInstant(from);
    const end = formatInstant(to);
    if (start === null || end === null) return '';
    return `${start} – ${end}`;
}

/** Un instante ISO como el día del negocio que le corresponde en CDMX, en `DD/mmm/YYYY`. */
function formatInstant(iso: string): string | null {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    // `formatToParts` saca el día del negocio en CDMX como números sueltos; el nombre del mes y el
    // separador los pone `formatBusinessDay`, que es donde vive la convención.
    const parts = new Intl.DateTimeFormat('en-CA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Mexico_City',
    }).formatToParts(date);
    const value = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    return formatBusinessDay(`${value('year')}-${value('month')}-${value('day')}`);
}
